/**
 * Injectable clock. Window gating, lease expiry, memory recency and budget
 * windows are all time-dependent, and untestable time is how those bugs reach
 * production. So every time-dependent path can be handed its "now"; what this
 * does NOT guarantee is that nothing calls `new Date()`. Plenty of code does,
 * as the default behind an injectable parameter (core-duplication/
 * clock-doc-claim-false: the old header said otherwise and was true only on
 * the day this file was added).
 *
 * Three shapes coexist, each for a reason:
 *
 * - `Clock` (this interface), for long-lived objects that observe time
 *   repeatedly across their lifetime: the send arbiter (`arbiter.ts`), the
 *   gateway and its circuit breakers (`packages/gateway`), the telemetry sink
 *   (`packages/db/src/telemetrySink.ts`). Tests drive them with `FixedClock`.
 * - `now?: () => Date`, a thunk, for a component that reads time more than
 *   once per run but is not worth a Clock: the reply agent, the reply graph
 *   and compose handler, the worker loop. `replyAgent.ts` (`#now`) says why:
 *   memory decay and the freshness gate must read ONE clock, or a fixed-clock
 *   eval stops being reproducible.
 * - `now?: Date` (or a required `now: Date`), a single instant, for one-shot
 *   loaders and pure functions. The optional form defaults to `new Date()`.
 *   The intended rule is that only the outermost IO caller defaults and pure
 *   functions take `now` as required, as TA-116 did for the stage 3 labeler;
 *   this header states that direction, it does not claim every signature
 *   already follows it.
 *
 * Why it matters: the repo has twice paid for a second wall-clock read inside
 * one run (TA-85 in `packages/evals/src/runner.ts`, TA-116 in stage 3). Read
 * "now" once per unit of work and pass it down.
 */
export interface Clock {
  now(): Date;
}

export const systemClock: Clock = { now: () => new Date() };

export class FixedClock implements Clock {
  constructor(private current: Date) {}
  now(): Date {
    return this.current;
  }
  set(d: Date): void {
    this.current = d;
  }
  advance(ms: number): void {
    this.current = new Date(this.current.getTime() + ms);
  }
}
